import React, { useState, useEffect } from 'react';
import { CreditCard, Plus, History, TrendingUp, Loader2, AlertCircle, FileText, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import AppHeader from '@/components/AppHeader';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const CreditsPage = () => {
  const { user } = useAuth();
  const { currentWorkspace, refreshWorkspace } = useWorkspace();
  const [loading, setLoading] = useState(false);
  const [creditHistory, setCreditHistory] = useState([]);
  const [payments, setPayments] = useState([]);
  const [purchasing, setPurchasing] = useState(false);
  const [activeTab, setActiveTab] = useState('credits');

  useEffect(() => {
    if (activeTab === 'history') fetchCreditHistory();
    if (activeTab === 'payments') fetchPayments();
  }, [activeTab, currentWorkspace]);

  const fetchCreditHistory = async () => {
    if (!currentWorkspace?._id && !currentWorkspace?.workspace_id) return;
    setLoading(true);
    try {
      const wsId = currentWorkspace?.workspace_id || currentWorkspace?._id;
      const response = await axios.get(`${API}/credits/history/${wsId}`);
      setCreditHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch credit history:', error);
      setCreditHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/payments/my-payments`);
      setPayments(response.data.payments || []);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBuyCredits = async (amount) => {
    if (!currentWorkspace?._id) {
      toast.error('No workspace selected');
      return;
    }

    setPurchasing(true);
    try {
      const response = await axios.post(`${API}/credits/purchase`, {
        workspace_id: currentWorkspace._id,
        amount: amount
      });

      if (response.data.payment_url) {
        window.location.href = response.data.payment_url;
      } else {
        toast.success('Credits added successfully!');
        refreshWorkspace();
        fetchCreditHistory();
      }
    } catch (error) {
      console.error('Failed to purchase credits:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate purchase');
    } finally {
      setPurchasing(false);
    }
  };

  const creditPackages = [
    { credits: 100, priceINR: 499, priceUSD: 6, popular: false, savings: null },
    { credits: 300, priceINR: 1299, priceUSD: 16, popular: true, savings: '13% off' },
    { credits: 500, priceINR: 1999, priceUSD: 24, popular: false, savings: '20% off' },
  ];

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader title="Credits & Billing" />
      
      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">

        {/* Current Balance Card */}
        <Card className="bg-gradient-to-br from-amber-50 to-teal-50 border-amber-200 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-500 text-sm mb-1">Current Balance</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-zinc-900" data-testid="credit-balance">
                    {currentWorkspace?.credits || 0}
                  </span>
                  <span className="text-zinc-500">credits</span>
                </div>
                <p className="text-zinc-400 text-sm mt-2">
                  Workspace: {currentWorkspace?.name || 'Loading...'}
                </p>
              </div>
              <CreditCard className="w-12 h-12 text-amber-500/40 hidden md:block" />
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-zinc-100 p-1 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab('credits')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'credits' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'
            }`}
          >
            Buy Credits
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'history' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'
            }`}
          >
            Credit History
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'payments' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'
            }`}
          >
            Payments & Invoices
          </button>
        </div>

        {/* Credit Packages Tab */}
        {activeTab === 'credits' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {creditPackages.map((pkg, index) => (
              <Card 
                key={index}
                className={`bg-white border-zinc-200 relative overflow-hidden ${
                  pkg.popular ? 'ring-2 ring-amber-500' : ''
                }`}
                data-testid={`credit-package-${pkg.credits}`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 right-0 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    POPULAR
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-zinc-900">{pkg.credits}</span>
                    <span className="text-zinc-500 ml-2">credits</span>
                  </div>
                  <div className="text-center mb-4">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-2xl font-semibold text-amber-600">${pkg.priceUSD}</span>
                      <span className="text-zinc-400">|</span>
                      <span className="text-2xl font-semibold text-amber-600">₹{pkg.priceINR}</span>
                    </div>
                    {pkg.savings && (
                      <span className="inline-block mt-2 text-sm text-green-600 bg-green-50 px-2 py-0.5 rounded">
                        {pkg.savings}
                      </span>
                    )}
                  </div>
                  <Button
                    onClick={() => handleBuyCredits(pkg.credits)}
                    disabled={purchasing}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid={`buy-credits-${pkg.credits}`}
                  >
                    {purchasing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buy Now'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Credit History Tab */}
        {activeTab === 'history' && (
          <Card className="bg-white border-zinc-200">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                </div>
              ) : creditHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <History className="w-12 h-12 text-zinc-300 mb-3" />
                  <p className="text-zinc-600">No credit transactions yet</p>
                  <p className="text-zinc-400 text-sm">Your credit purchases and usage will appear here</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {creditHistory.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 hover:bg-zinc-50">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          item.amount > 0 ? 'bg-amber-100' : 'bg-amber-100'
                        }`}>
                          {item.amount > 0 ? (
                            <TrendingUp className="w-5 h-5 text-amber-500" />
                          ) : (
                            <CreditCard className="w-5 h-5 text-amber-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-zinc-900 font-medium">{item.description}</p>
                          <p className="text-zinc-400 text-sm">
                            {new Date(item.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric', month: 'short', year: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      <span className={`font-semibold ${item.amount > 0 ? 'text-amber-500' : 'text-amber-600'}`}>
                        {item.amount > 0 ? '+' : ''}{item.amount} credits
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payments & Invoices Tab */}
        {activeTab === 'payments' && (
          <Card className="bg-white border-zinc-200">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                </div>
              ) : payments.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <Receipt className="w-12 h-12 text-zinc-300 mb-3" />
                  <p className="text-zinc-600">No payments yet</p>
                  <p className="text-zinc-400 text-sm">Your payment history and invoices will appear here</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {payments.map((payment, index) => (
                    <div key={index} className="flex items-center justify-between p-4 hover:bg-zinc-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-zinc-900 font-medium">{payment.description || 'Payment'}</p>
                          <p className="text-zinc-400 text-sm">
                            {new Date(payment.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric', month: 'short', year: 'numeric'
                            })}
                            {payment.invoice_id && <span className="ml-2">• Invoice #{payment.invoice_id}</span>}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-zinc-900">₹{payment.amount?.toLocaleString()}</p>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          payment.status === 'paid' ? 'bg-amber-100 text-amber-600' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {payment.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
        </div>
      </div>
    </div>
  );
};

export default CreditsPage;
